import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Inbox, Mail, Store, Check, Trash2, Globe } from "lucide-react";

export default function LeadsPage() {
  const [leads, setLeads] = useState(null);
  const [converting, setConverting] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/signups").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const convert = async (lead) => {
    setConverting(lead.id);
    try {
      const res = await api.post(`/signups/${lead.id}/convert`);
      toast.success(`Store "@${res.data.handle}" created`);
      await load();
      navigate("/admin/stores");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not convert lead");
    } finally { setConverting(null); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    try {
      await api.delete(`/signups/${lead.id}`);
      toast.success("Lead deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete lead"); }
  };

  if (!leads) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="animate-fade-up" data-testid="leads-page">
      <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Growth</span>
      <h1 className="font-heading font-light text-4xl tracking-tight mt-1 mb-2">Leads</h1>
      <p className="text-zinc-500 font-body mb-6">Merchant sign-up requests from your landing page. Convert a lead to spin up their storefront in one click.</p>

      {leads.length === 0 ? (
        <div className="bg-white border border-zinc-200/80 rounded-xl p-16 text-center">
          <Inbox className="mx-auto text-zinc-300" size={40} />
          <p className="text-zinc-400 font-body mt-4">No sign-up requests yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[880px]">
            <thead className="bg-zinc-50 text-zinc-400 text-xs uppercase tracking-wider font-heading">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Name</th>
                <th className="text-left px-5 py-3 font-bold">Email</th>
                <th className="text-left px-5 py-3 font-bold">Location</th>
                <th className="text-left px-5 py-3 font-bold">Store</th>
                <th className="text-left px-5 py-3 font-bold">Message</th>
                <th className="text-left px-5 py-3 font-bold">Date</th>
                <th className="px-5 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`lead-${l.id}`}>
                  <td className="px-5 py-4 font-body font-semibold">{l.name}</td>
                  <td className="px-5 py-4 text-zinc-500">{l.email}</td>
                  <td className="px-5 py-4 text-zinc-500" data-testid={`lead-geo-${l.id}`}>
                    {l.geo ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-base leading-none">{l.geo.flag}</span>
                        <span>{[l.geo.city, l.geo.country_code].filter(Boolean).join(", ") || "—"}</span>
                        {l.geo_flag && <span title="Entered country does not match detected location" data-testid={`lead-geoflag-${l.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">⚠ mismatch</span>}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-4 text-zinc-500">{l.store_name || "—"}</td>
                  <td className="px-5 py-4 text-zinc-500 max-w-xs truncate">{l.message || "—"}</td>
                  <td className="px-5 py-4 text-zinc-500">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50" data-testid={`lead-reply-${l.id}`}>
                        <Mail size={12} /> Reply
                      </a>
                      {l.website ? (
                        <button
                          onClick={() => navigate(`/admin/products?scrape=${encodeURIComponent(l.website)}`)}
                          data-testid={`lead-scrape-${l.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50"
                          title={`Scrape products from ${l.website}`}
                        >
                          <Globe size={12} /> Scrape shop
                        </button>
                      ) : null}
                      {l.status === "converted" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 rounded-full px-3 py-1.5" data-testid={`lead-converted-${l.id}`}>
                          <Check size={12} /> Converted
                        </span>
                      ) : (
                        <button
                          onClick={() => convert(l)}
                          disabled={converting === l.id}
                          data-testid={`lead-convert-${l.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-black rounded-full px-3 py-1.5 active:scale-95 transition-transform disabled:opacity-60"
                        >
                          {converting === l.id ? <Loader2 size={12} className="animate-spin" /> : <Store size={12} />} Convert to store
                        </button>
                      )}
                      <button onClick={() => remove(l)} data-testid={`lead-delete-${l.id}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-zinc-200 text-zinc-400 hover:text-rose-500 hover:border-rose-200 transition-colors" title="Delete lead">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
